import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { clsx } from 'clsx'
import type { ReactNode } from 'react'

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@quebon/ui/components/dialog'

interface InquiryDialogProps {
  open: boolean
  onClose: () => void
  title: string
  /** Tabs row rendered between header and body. Optional. */
  tabs?: ReactNode
  /** Sticky footer (typically a CTA button). */
  footer?: ReactNode
  children: ReactNode
}

export function InquiryDialog({
  open,
  onClose,
  title,
  tabs,
  footer,
  children,
}: InquiryDialogProps) {
  const { t: mt } = useTranslation('marketing')

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        className={clsx(
          'w-full max-w-[calc(100%-2rem)] sm:max-w-[480px] max-h-[90vh] overflow-hidden',
          'flex flex-col p-0 gap-0',
          'bg-white rounded-[24px] shadow-[0px_8px_24px_0px_rgba(16,24,40,0.10)]',
          'ring-0 text-inherit',
          'z-[1001]',
        )}
        style={{ zIndex: 1001 }}
      >
        <div className="flex items-start justify-between pt-8 pb-4 px-9 shrink-0">
          <DialogTitle className="text-[24px] font-bold text-qb-text-high leading-[1.4] tracking-[-0.6px]">
            {title}
          </DialogTitle>
          <button
            type="button"
            aria-label={mt('certificate.close')}
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-[10px] bg-[#f7f7f7] text-qb-text-low hover:text-qb-text-high transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {tabs}

        <div className="overflow-y-auto flex-1 min-h-0">{children}</div>

        {footer && <div className="shrink-0 px-8 pt-5 pb-8">{footer}</div>}
      </DialogContent>
    </Dialog>
  )
}
